<script lang="ts">
const field = ref<Field | null>(null)
export function useRecomputeDismissReviewModal() {
  return {
    setOpen(f: Field) {
      field.value = f
    },
    setClosed() {
      field.value = null
    },
    field,
  }
}
</script>

<script setup lang="ts">
import { useSetFieldReviewStatus } from '@/data/Fields/useSetFieldReviewStatus'
import type { Field } from '@/modules/Project/Fields/types'
import { serializeField } from '@/modules/Project/serializer'
import { useFieldRecompute } from '@/modules/Project/useFieldRecompute'
import { invariant } from '@/shared/utils/typeAssertions'
import CheckBox from '@/uiKit/CheckBox.vue'
import ConfirmationDialog from '@/uiKit/ConfirmationDialog.vue'
import { ref, toValue, useId, watch } from 'vue'

/**
 * This modal dialog is shown when the user tries to recompute a field that has been
 * reviewed. It allows the user to dismiss the review and recalculate the field.
 */

const { setClosed, field } = useRecomputeDismissReviewModal()

const hasAcknowledged = ref(false)

const inputId = useId()

const isRecomputing = ref(false)
const { recomputeField } = useFieldRecompute()
const { mutate, isPending: isDismissingReview } = useSetFieldReviewStatus({
  onSuccess: async (fieldResponse, params) => {
    const field = serializeField(
      fieldResponse,
      toValue(params.workspaceId),
      toValue(params.projectId),
    )
    isRecomputing.value = true
    try {
      await recomputeField(field)
    } finally {
      isRecomputing.value = false
    }
    setClosed()
  },
})

const onConfirm = () => {
  invariant(field.value)
  mutate({
    workspaceId: field.value.workspaceId,
    projectId: field.value.projectId,
    propertyId: field.value.propertyId,
    entityId: field.value.entityId,
    reviewStatus: 'pending',
  })
}

watch(field, () => {
  hasAcknowledged.value = false
  isRecomputing.value = false
})
</script>

<template>
  <ConfirmationDialog
    :open="!!field"
    confirm-text="Dismiss review and recalculate"
    :confirm-loading="isDismissingReview || isRecomputing"
    :disable-actions="hasAcknowledged ? false : 'confirm'"
    variant="critical"
    title="Dismiss review and recalculate?"
    @close="setClosed"
    @confirm="onConfirm"
  >
    <p class="mb-2 ml-1 max-w-prose text-sm-12px-light">
      This field has been reviewed. To recalculate its value, the review will be dismissed and the
      field will be recomputed. This action cannot be undone.
    </p>
    <div class="flex gap-2">
      <CheckBox
        :checked="hasAcknowledged"
        :input-attrs="{ id: inputId }"
        @change="hasAcknowledged = $event"
      />
      <label
        :for="inputId"
        class="text-sm-12px-light"
        >I want to dismiss the review</label
      >
    </div>
  </ConfirmationDialog>
</template>
